/* 
Generated code: do not modify this file as it will be overwritten
Template: ImmutableJSRecord.cst

Known Issues/TODO:
- Using *Props type as Record constructor parameter is insufficient.
If a property needs to be a Record type, it should already be constructed.
- Rename Record fromJS factory method to fromJSON
- Create parseJSON method that translates JSON to Props



 */

/* eslint-disable import/order */

import { createSelector } from '@ngrx/store';
import { List, Map, Record, Seq, Set } from 'immutable';
import { EntityType, NotificationStatus } from 'src/graphql/generated/graphql.generated';
import { AppState } from 'src/store/appstate';
import { getFromOrThrow, propValueOrDefault, tryOrDefault } from 'src/util/values';

export interface NotificationProps {
  id: string;
  cardId: string | null;
  status: NotificationStatus;
  subject: string | null;
  body: string;
  sourceTemplateId: string | null;
  entityType: EntityType | null;
  entityId: string | null;
  createdById: string | null;
  companyId: string;
  dateLastUpdated: Date;
  dateCreated: Date;
  dateDeleted: Date | null;
  lastUpdatedById: string | null;
}

export class NotificationRecord extends Record<NotificationProps>({
  id: null as any,
  cardId: null as any,
  status: null as any,
  subject: null as any,
  body: null as any,
  sourceTemplateId: null as any,
  entityType: null as any,
  entityId: null as any,
  createdById: null as any,
  companyId: null as any,
  dateLastUpdated: null as any,
  dateCreated: null as any,
  dateDeleted: null as any,
  lastUpdatedById: null as any,
}) {
  public static fromJS(json: {
    id: string;
    cardId: string | null;
    status: NotificationStatus;
    subject: string | null;
    body: string;
    sourceTemplateId: string | null;
    entityType: EntityType | null;
    entityId: string | null;
    createdById: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): NotificationRecord {
    if (json == null) {
      throw new Error('NotificationRecord.fromJS: parameter cannot be null');
    }
    return new NotificationRecord({
      id: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
        'NotificationJson.id',
      ),
      cardId: json.cardId,
      status: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'status', NotificationStatus.Draft),
        'NotificationJson.status',
      ),
      subject: json.subject,
      body: getFromOrThrow(json, (j) => j.body, 'NotificationJson.body'),
      sourceTemplateId: json.sourceTemplateId,
      entityType: json.entityType,
      entityId: json.entityId,
      createdById: json.createdById,
      companyId: getFromOrThrow(
        json,
        (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
        'NotificationJson.companyId',
      ),
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });
  }

  public static fromJSUnsafe(json: {
    id: string;
    cardId: string | null;
    status: NotificationStatus;
    subject: string | null;
    body: string;
    sourceTemplateId: string | null;
    entityType: EntityType | null;
    entityId: string | null;
    createdById: string | null;
    companyId: string;
    dateLastUpdated: string | number | Date;
    dateCreated: string | number | Date;
    dateDeleted: string | number | Date | null;
    lastUpdatedById: string | null;
  }): [NotificationRecord, Error[]] {
    if (json == null) {
      throw new Error('NotificationRecord.fromJSUnsafe: parameter cannot be null');
    }
    const errors: Error[] = [];

    const record = new NotificationRecord({
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      id: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'id', '00000000-0000-0000-0000-000000000000'),
            'NotificationJson.id',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      cardId: tryOrDefault(
        () => json.cardId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      status: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'status', NotificationStatus.Draft),
            'NotificationJson.status',
          ),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      subject: tryOrDefault(
        () => json.subject,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      body: tryOrDefault(
        () => getFromOrThrow(json, (j) => j.body, 'NotificationJson.body'),
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      sourceTemplateId: tryOrDefault(
        () => json.sourceTemplateId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityType: tryOrDefault(
        () => json.entityType,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      entityId: tryOrDefault(
        () => json.entityId,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      createdById: tryOrDefault(
        () => json.createdById,
        (err) => errors.push(err),
      )!,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      companyId: tryOrDefault(
        () =>
          getFromOrThrow(
            json,
            (j) => propValueOrDefault(j, 'companyId', '00000000-0000-0000-0000-000000000000'),
            'NotificationJson.companyId',
          ),
        (err) => errors.push(err),
      )!,
      dateLastUpdated: new Date(json.dateLastUpdated),
      dateCreated: new Date(json.dateCreated),
      dateDeleted: json.dateDeleted != null ? new Date(json.dateDeleted) : null,
      lastUpdatedById: json.lastUpdatedById,
    });

    return [record, errors];
  }

  constructor(props: NotificationProps) {
    super(props);
    if (props == null) {
      throw new Error('NotificationRecord constructor parameter cannot be null');
    }
  }
}

export abstract class NotificationViewModelBase implements NotificationProps {
  protected readonly _props: NotificationProps;
  private _json?: NotificationProps;

  get id(): string {
    return this._props.id;
  }
  get cardId(): string | null {
    return this._props.cardId;
  }
  get status(): NotificationStatus {
    return this._props.status;
  }
  get subject(): string | null {
    return this._props.subject;
  }
  get body(): string {
    return this._props.body;
  }
  get sourceTemplateId(): string | null {
    return this._props.sourceTemplateId;
  }
  get entityType(): EntityType | null {
    return this._props.entityType;
  }
  get entityId(): string | null {
    return this._props.entityId;
  }
  get createdById(): string | null {
    return this._props.createdById;
  }
  get companyId(): string {
    return this._props.companyId;
  }

  get dateCreated(): Date {
    return this._props.dateCreated;
  }
  get dateLastUpdated(): Date {
    return this._props.dateLastUpdated;
  }
  get dateDeleted(): Date | null {
    return this._props.dateDeleted;
  }
  get lastUpdatedById(): string | null {
    return this._props.lastUpdatedById;
  }

  public get isAwaitingApproval() {
    return this._props.status === NotificationStatus.AwaitingApproval;
  }
  public get isCancelled() {
    return this._props.status === NotificationStatus.Cancelled;
  }
  public get isDraft() {
    return this._props.status === NotificationStatus.Draft;
  }
  public get isFailed() {
    return this._props.status === NotificationStatus.Failed;
  }
  public get isPartiallyFailed() {
    return this._props.status === NotificationStatus.PartiallyFailed;
  }
  public get isReceived() {
    return this._props.status === NotificationStatus.Received;
  }
  public get isSending() {
    return this._props.status === NotificationStatus.Sending;
  }
  public get isSent() {
    return this._props.status === NotificationStatus.Sent;
  }
  public get isAirport() {
    return this._props.entityType === EntityType.Airport;
  }
  public get isBase() {
    return this._props.entityType === EntityType.Base;
  }
  public get isCard() {
    return this._props.entityType === EntityType.Card;
  }
  public get isCommunicationCenter() {
    return this._props.entityType === EntityType.CommunicationCenter;
  }
  public get isCompany() {
    return this._props.entityType === EntityType.Company;
  }
  public get isControlZone() {
    return this._props.entityType === EntityType.ControlZone;
  }
  public get isLocation() {
    return this._props.entityType === EntityType.Location;
  }
  public get isMedicalFacility() {
    return this._props.entityType === EntityType.MedicalFacility;
  }
  public get isOutOfServiceEvent() {
    return this._props.entityType === EntityType.OutOfServiceEvent;
  }
  public get isPaipEvent() {
    return this._props.entityType === EntityType.PaipEvent;
  }
  public get isPerson() {
    return this._props.entityType === EntityType.Person;
  }
  public get isProgram() {
    return this._props.entityType === EntityType.Program;
  }
  public get isRegion() {
    return this._props.entityType === EntityType.Region;
  }
  public get isTimer() {
    return this._props.entityType === EntityType.Timer;
  }
  public get isUnit() {
    return this._props.entityType === EntityType.Unit;
  }
  public get isUnspecified() {
    return this._props.entityType === EntityType.Unspecified;
  }
  public get isVehicle() {
    return this._props.entityType === EntityType.Vehicle;
  }

  public isAnyStatus(...notificationStatus: NotificationStatus[]) {
    return notificationStatus.includes(this._props.status);
  }
  public isAnyEntityType(...entityType: EntityType[]) {
    return this._props.entityType != null && entityType.includes(this._props.entityType);
  }

  constructor(props: NotificationProps) {
    this._props = props;
  }

  public toJS() {
    return this.toJSON();
  }

  public toJSON() {
    if (this._json == null) {
      this._json = {
        id: this._props.id,
        cardId: this._props.cardId,
        status: this._props.status,
        subject: this._props.subject,
        body: this._props.body,
        sourceTemplateId: this._props.sourceTemplateId,
        entityType: this._props.entityType,
        entityId: this._props.entityId,
        createdById: this._props.createdById,
        companyId: this._props.companyId,
        dateLastUpdated: this._props.dateLastUpdated,
        dateCreated: this._props.dateCreated,
        dateDeleted: this._props.dateDeleted,
        lastUpdatedById: this._props.lastUpdatedById,
      };
    }
    return this._json;
  }
}

//#region Selectors

export const selectAllNotificationEntities = (state: AppState) =>
  state.entityStores.notification.entities;

export const selectNotificationById = (id: string) => (state: AppState) =>
  state.entityStores.notification.entities.get(id);

export const selectNotificationMetadataById = (id: string) => (state: AppState) =>
  state.entityStores.notification.meta.get(id);

export const selectNotificationAndMetadataById = (id: string) =>
  createSelector(
    selectNotificationById(id),
    selectNotificationMetadataById(id),
    (notification, meta) => ({ notification, meta }),
  );

export function selectNotificationEntitiesByIds(ids: string[]) {
  return createSelector(
    selectAllNotificationEntities,

    (entities) => {
      // TODO: Once we're on TS 3.5+, I don't think we'll need use type assertions
      const entries = ids.map((id) => [id, entities.get(id)]) as unknown as Iterable<
        [string, NotificationRecord | undefined]
      >;
      return Map(entries);
    },
  );
}

export function selectNotificationEntitiesByCardId(cardId: string) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.cardId === cardId).valueSeq(),
  );
}

export function selectNotificationEntitiesByStatus(status: NotificationStatus) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.status === status).valueSeq(),
  );
}

export function selectNotificationEntitiesBySubject(subject: string) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.subject === subject).valueSeq(),
  );
}

export function selectNotificationEntitiesByBody(body: string) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.body === body).valueSeq(),
  );
}

export function selectNotificationEntitiesBySourceTemplateId(sourceTemplateId: string) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.sourceTemplateId === sourceTemplateId).valueSeq(),
  );
}

export function selectNotificationEntitiesByEntityType(entityType: EntityType) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.entityType === entityType).valueSeq(),
  );
}

export function selectNotificationEntitiesByEntityId(entityId: string) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.entityId === entityId).valueSeq(),
  );
}

export function selectNotificationEntitiesByCreatedById(createdById: string) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.createdById === createdById).valueSeq(),
  );
}

export function selectNotificationEntitiesByCompanyId(companyId: string) {
  return createSelector(selectAllNotificationEntities, (entities) =>
    entities.filter((e) => e.companyId === companyId).valueSeq(),
  );
}
//#endregion Selectors
